import React from 'react';

/**
 * Bottom-navigation icons (JAA-266). Each destination has two glyph variants:
 * an outline glyph (the default, muted state) and a filled glyph (shown while
 * the tab is active). Both are exported verbatim from the Figma "Navigation
 * MASTER" component as single-colour glyphs whose colour is inherited from the
 * parent via `fill="currentColor"`, so active/muted colour is driven entirely
 * by CSS on `.jaakko-nav__tab`.
 *
 * Sizing is owned by CSS off the exact `.jaakko-nav__icon` box (25/18/24px):
 * the SVGs intentionally carry NO `width`/`height` attribute. A percentage
 * `width="100%"` leaves the SVG with no definite intrinsic size, so it
 * rasterizes at a fallback and scales up -> blurry; the CSS box provides the
 * definite size instead. Figma's "export verbatim" keeps re-adding width/height
 * (and `fill="none"` on the root); strip those.
 *
 * The non-square Messages viewBox and the offset viewBoxes on the filled
 * Explore/Activity glyphs (their coordinates are lifted straight from the Figma
 * artboard rather than re-zeroed) are centered via `preserveAspectRatio`.
 *
 * The filled Messages glyph has no Figma source, so it is composed here from the
 * outline silhouette: the solid bubble contour with the two text lines knocked
 * out via `fillRule="evenodd"`.
 */

type NavIconProps = {
  className?: string;
};

const svgProps = {
  fill: 'currentColor',
  preserveAspectRatio: 'xMidYMid meet' as const,
  xmlns: 'http://www.w3.org/2000/svg',
  'aria-hidden': true,
  focusable: false,
} as const;

type NavGlyphProps = NavIconProps & {
  viewBox: string;
  children: React.ReactNode;
};

const NavGlyph: React.FC<NavGlyphProps> = ({ className, viewBox, children }) => (
  <svg className={className} viewBox={viewBox} {...svgProps}>
    {children}
  </svg>
);

export const ExploreNavIcon: React.FC<NavIconProps> = ({ className }) => (
  <NavGlyph className={className} viewBox="0 0 25 25">
    <g clipPath="url(#nav-explore-outline-clip)">
      <path d="M22.7623 4.4613L19.0019 2.60344C17.5969 1.91083 15.9218 1.91083 14.5169 2.60344L9.23911 5.21096L4.0933 2.66863C3.31093 2.28259 2.39864 2.32232 1.65337 2.7766C0.909125 3.23189 0.464844 4.01924 0.464844 4.88399V15.0656C0.464844 16.954 1.52657 18.6519 3.23672 19.4973L6.99818 21.3552C7.70017 21.7015 8.47017 21.8757 9.24019 21.8757C10.0102 21.8757 10.7812 21.7015 11.4822 21.3552L16.76 18.7466L21.9079 21.2889C22.6903 21.677 23.6025 21.6332 24.3458 21.1799C25.09 20.7257 25.5343 19.9373 25.5343 19.0725V8.89202C25.5343 7.00258 24.4725 5.30561 22.7623 4.4613ZM4.35824 17.2809C3.50267 16.8592 2.9718 16.0087 2.9718 15.0655V4.88395L7.98567 7.36109V19.0735L4.35824 17.2809ZM10.4926 19.0746V7.36109L15.5064 4.88395V16.5954L10.4926 19.0746ZM23.0272 19.0736L18.0134 16.5964V4.88397L21.6418 6.67664C22.4964 7.09833 23.0272 7.94781 23.0272 8.89202V19.0736Z" />
    </g>
    <defs>
      <clipPath id="nav-explore-outline-clip">
        <rect width="25" height="25" />
      </clipPath>
    </defs>
  </NavGlyph>
);

export const ExploreNavIconFilled: React.FC<NavIconProps> = ({ className }) => (
  <NavGlyph className={className} viewBox="50.875 36.5 25 25">
    <path d="M52.0601 39.276C52.8033 38.8217 53.7132 38.7821 54.4934 39.1681L59.2083 41.5027V58.3556C58.5817 58.3038 57.9634 58.1374 57.3905 57.854L53.6389 55.9965C51.9336 55.1511 50.875 53.4527 50.875 51.5644V41.3837C50.875 40.519 51.318 39.7313 52.0601 39.276Z" />
    <path d="M66.5 55.5551L61.8623 57.854C61.3531 58.1064 60.806 58.2664 60.25 58.3352V41.4L64.8877 39.103C65.3978 38.8508 65.9444 38.6914 66.5 38.6229V55.5551Z" />
    <path d="M67.5417 38.6025C68.1684 38.654 68.787 38.8195 69.3605 39.103L73.1111 40.9605C74.8163 41.8049 75.875 43.5024 75.875 45.3917V55.5713C75.875 56.436 75.432 57.2248 74.6899 57.6791C73.9487 58.1323 73.0389 58.176 72.2587 57.7879L67.5417 55.4523V38.6025Z" />
  </NavGlyph>
);

export const ActivityNavIcon: React.FC<NavIconProps> = ({ className }) => (
  <NavGlyph className={className} viewBox="0 0 25 25">
    <path d="M10.4164 19.791C10.4164 20.9416 11.3491 21.8743 12.4997 21.8743C13.6502 21.8742 14.583 20.9415 14.583 19.791H10.4164ZM12.4997 3.12435C9.04791 3.12435 6.24969 5.92257 6.24969 9.37435V14.8228L3.64654 17.7077H21.3549L18.7497 14.8217V9.37435C18.7497 5.92269 15.9514 3.12449 12.4997 3.12435ZM16.6664 19.791C16.6664 22.0921 14.8008 23.9576 12.4997 23.9577C10.1985 23.9577 8.33302 22.0922 8.33302 19.791H2.47466L2.34954 19.7859C1.07771 19.6908 0.436105 18.1553 1.31499 17.1818L4.16635 14.0222V9.37435C4.16635 4.77198 7.89731 1.04102 12.4997 1.04102L12.9279 1.05221C17.3312 1.27525 20.833 4.9157 20.833 9.37435V14.0201L23.6864 17.1818C24.5648 18.1552 23.9233 19.6905 22.6519 19.7859L22.5267 19.791H16.6664Z" />
  </NavGlyph>
);

export const ActivityNavIconFilled: React.FC<NavIconProps> = ({ className }) => (
  <NavGlyph className={className} viewBox="139.625 36.5 25 25">
    <path d="M156.161 57.3337C155.699 59.1307 154.067 60.4586 152.126 60.4587C150.184 60.4587 148.553 59.1307 148.09 57.3337H156.161ZM152.125 37.542C156.727 37.5421 160.458 41.273 160.458 45.8753V50.5211L163.311 53.6827C164.218 54.6876 163.505 56.2917 162.152 56.292H142.1C140.746 56.292 140.033 54.6877 140.94 53.6827L143.791 50.5232V45.8753C143.791 41.273 147.522 37.542 152.125 37.542Z" />
  </NavGlyph>
);

export const MessagesNavIcon: React.FC<NavIconProps> = ({ className }) => (
  <NavGlyph className={className} viewBox="0 0 23 24">
    <path d="M12.5 10.4167C13.0753 10.4167 13.5417 10.883 13.5417 11.4583C13.5417 12.0336 13.0753 12.5 12.5 12.5H7.29167C6.71637 12.5 6.25 12.0336 6.25 11.4583C6.25 10.883 6.71637 10.4167 7.29167 10.4167H12.5Z" />
    <path d="M15.625 6.25C16.2003 6.25 16.6667 6.71637 16.6667 7.29167C16.6667 7.86696 16.2003 8.33333 15.625 8.33333H7.29167C6.71637 8.33333 6.25 7.86696 6.25 7.29167C6.25 6.71637 6.71637 6.25 7.29167 6.25H15.625Z" />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M13.0208 0C18.4862 6.71125e-08 22.9167 4.43052 22.9167 9.89583C22.9167 15.3612 18.4862 19.7917 13.0208 19.7917H11.5621C11.5049 19.7919 11.4586 19.8382 11.4583 19.8954V23.0469C11.4583 23.5503 11.0503 23.9583 10.5469 23.9583C7.17198 23.9583 4.40925 21.3378 4.18193 18.0206C4.18084 18.0051 4.17608 17.9903 4.1687 17.9769L4.13818 17.9423C1.63344 16.1468 0 13.2128 0 9.89583C1.34224e-07 4.43052 4.43052 6.71084e-08 9.89583 0H13.0208ZM9.89583 2.08333C5.58111 2.08333 2.08333 5.58111 2.08333 9.89583C2.08333 12.5116 3.36846 14.8268 5.35177 16.2486C5.88821 16.6331 6.21597 17.2339 6.26017 17.8782C6.38577 19.711 7.6642 21.2269 9.375 21.7112V19.8954C9.37522 18.6876 10.3543 17.7086 11.5621 17.7083H13.0208C17.3356 17.7083 20.8333 14.2106 20.8333 9.89583C20.8333 5.58111 17.3356 2.08333 13.0208 2.08333H9.89583Z"
    />
  </NavGlyph>
);

export const MessagesNavIconFilled: React.FC<NavIconProps> = ({ className }) => (
  <NavGlyph className={className} viewBox="0 0 23 24">
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M13.0208 0C18.4862 6.71125e-08 22.9167 4.43052 22.9167 9.89583C22.9167 15.3612 18.4862 19.7917 13.0208 19.7917H11.5621C11.5049 19.7919 11.4586 19.8382 11.4583 19.8954V23.0469C11.4583 23.5503 11.0503 23.9583 10.5469 23.9583C7.17198 23.9583 4.40925 21.3378 4.18193 18.0206C4.18084 18.0051 4.17608 17.9903 4.1687 17.9769L4.13818 17.9423C1.63344 16.1468 0 13.2128 0 9.89583C1.34224e-07 4.43052 4.43052 6.71084e-08 9.89583 0H13.0208ZM12.5 10.4167H7.29167C6.71637 10.4167 6.25 10.883 6.25 11.4583C6.25 12.0336 6.71637 12.5 7.29167 12.5H12.5C13.0753 12.5 13.5417 12.0336 13.5417 11.4583C13.5417 10.883 13.0753 10.4167 12.5 10.4167ZM15.625 6.25H7.29167C6.71637 6.25 6.25 6.71637 6.25 7.29167C6.25 7.86696 6.71637 8.33333 7.29167 8.33333H15.625C16.2003 8.33333 16.6667 7.86696 16.6667 7.29167C16.6667 6.71637 16.2003 6.25 15.625 6.25Z"
    />
  </NavGlyph>
);

export const ToolsNavIcon: React.FC<NavIconProps> = ({ className }) => (
  <NavGlyph className={className} viewBox="0 0 25 25">
    <g clipPath="url(#nav-tools-outline-clip)">
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M11.8893 2.80167C12.3556 2.46493 13.0071 2.56958 13.344 3.03564C13.6806 3.50188 13.576 4.15345 13.11 4.49031L4.81433 10.4819C4.40768 10.7757 4.16634 11.2467 4.16634 11.7484V20.3127C4.1665 21.1755 4.86599 21.8752 5.72884 21.8752H8.33301V16.146C8.33301 14.7078 9.49893 13.5418 10.9372 13.5418H14.0622C15.5004 13.5418 16.6663 14.7078 16.6663 16.146V21.8752H19.2705C20.1334 21.8752 20.8329 21.1755 20.833 20.3127V12.5002C20.833 11.9249 21.2994 11.4585 21.8747 11.4585C22.45 11.4585 22.9163 11.9249 22.9163 12.5002V20.3127C22.9162 22.3261 21.284 23.9585 19.2705 23.9585H5.72884C3.7154 23.9585 2.08316 22.3261 2.08301 20.3127V11.7484C2.08301 10.5778 2.64567 9.47771 3.59465 8.79227L11.8893 2.80167ZM10.9372 15.6252C10.6495 15.6252 10.4163 15.8583 10.4163 16.146V21.8752H14.583V16.146C14.583 15.8583 14.3498 15.6252 14.0622 15.6252H10.9372Z"
      />
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M18.5767 0.771236C18.9032 0.281984 20.6795 0.281983 21.0059 0.771236C21.8329 2.01461 22.9852 3.16698 24.2286 3.99389C24.7179 4.32022 24.7179 6.0956 24.2286 6.42207L23.7668 6.74759C22.7018 7.53913 21.7296 8.55759 21.0059 9.64575C20.6898 10.1198 19.0133 10.134 18.6123 9.68949L18.5767 9.64575C17.7497 8.40214 16.5977 7.2491 15.3541 6.42207L15.3103 6.38749C14.8659 5.98644 14.88 4.31005 15.3541 3.99389C16.5975 3.16698 17.7498 2.01461 18.5767 0.771236ZM19.7913 2.64908C19.0605 3.59932 18.1841 4.47655 17.234 5.20747C18.184 5.93819 19.0605 6.81486 19.7913 7.76485C20.5221 6.81486 21.3987 5.93819 22.3487 5.20747C21.3985 4.47655 20.5222 3.59932 19.7913 2.64908Z"
      />
    </g>
    <defs>
      <clipPath id="nav-tools-outline-clip">
        <rect width="25" height="25" />
      </clipPath>
    </defs>
  </NavGlyph>
);

export const ToolsNavIconFilled: React.FC<NavIconProps> = ({ className }) => (
  <NavGlyph className={className} viewBox="0 0 25 25">
    <g clipPath="url(#nav-tools-filled-clip)">
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M11.8893 2.80167C12.3556 2.46493 13.0071 2.56958 13.344 3.03564C13.6806 3.50188 13.576 4.15345 13.11 4.49031L4.81433 10.4819C4.40768 10.7757 4.16634 11.2467 4.16634 11.7484V20.3127C4.1665 21.1755 4.86599 21.8752 5.72884 21.8752H8.33301V16.146C8.33301 14.7078 9.49893 13.5418 10.9372 13.5418H14.0622C15.5004 13.5418 16.6663 14.7078 16.6663 16.146V21.8752H19.2705C20.1334 21.8752 20.8329 21.1755 20.833 20.3127V12.5002C20.833 11.9249 21.2994 11.4585 21.8747 11.4585C22.45 11.4585 22.9163 11.9249 22.9163 12.5002V20.3127C22.9162 22.3261 21.284 23.9585 19.2705 23.9585H5.72884C3.7154 23.9585 2.08316 22.3261 2.08301 20.3127V11.7484C2.08301 10.5778 2.64567 9.47771 3.59465 8.79227L11.8893 2.80167ZM10.9372 15.6252C10.6495 15.6252 10.4163 15.8583 10.4163 16.146V21.8752H14.583V16.146C14.583 15.8583 14.3498 15.6252 14.0622 15.6252H10.9372Z"
      />
      <path d="M18.5767 0.771236C18.9032 0.281984 20.6795 0.281983 21.0059 0.771236C21.8329 2.01461 22.9852 3.16698 24.2286 3.99389C24.7179 4.32022 24.7179 6.0956 24.2286 6.42207L23.7668 6.74759C22.7018 7.53913 21.7296 8.55759 21.0059 9.64575C20.6898 10.1198 19.0133 10.134 18.6123 9.68949L18.5767 9.64575C17.7497 8.40214 16.5977 7.2491 15.3541 6.42207L15.3103 6.38749C14.8659 5.98644 14.88 4.31005 15.3541 3.99389C16.5975 3.16698 17.7498 2.01461 18.5767 0.771236Z" />
    </g>
    <defs>
      <clipPath id="nav-tools-filled-clip">
        <rect width="25" height="25" />
      </clipPath>
    </defs>
  </NavGlyph>
);
